import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import { Divider, InputAdornment, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Footer from 'components/common/Footer/Footer';
import HeaderLogo from 'components/common/HeaderLogo/HeaderLogo';
import ExternalClientAccessInfo from 'components/common/ExternalClientAccessInfo/ExternalClientAccessInfo';
import NumberFormatCustom from 'components/common/NumberFormatCustom';
import Summary from 'components/common/Summary/Summary';
import WarningMessage from 'components/common/WarningMessage/WarningMessage';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useValidator } from 'hooks/useValidator';
import actions from 'store/actions';
import { updateNA, updateExternalAccessNA } from 'store/na/thunk';
import styles from './step3.module.scss';
import { dateParse } from 'utils/date';
import { ReactComponent as CalendarIcon } from 'icons/calendar.svg';
import { validationConfig } from 'config/validationConfig';
import { useTranslation } from 'react-i18next';
import { weakKey } from 'utils/objects';
import searchParams from 'constants/searchParams';
import paths from 'constants/paths';
import { useStepsWizard } from 'hooks/useStepsWizard';

export default function Step3() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = new URLSearchParams(window.location.search);
  const preview = params.get(searchParams.preview);
  const na = useSelector((state) => state.na);
  const isMounted = useRef(false);
  const isExternalAccess = useSelector((state) => state.general.isExternalAccessMode);
  const isReadOnlyMode = useSelector((state) => state.general.isReadOnlyMode);
  const externalAccessConfig = useSelector((state) => state.externalAccessConfig);
  const [headcount, setHeadcount] = useState(na.headcount);
  const [startingYear, setStartingYear] = useState(new Date(na.startingYear));
  const [leaseTermInYears, setLeaseTermInYears] = useState(na.leaseTermInYears);
  const [defaultGrowth, setDefaultGrowth] = useState(na.defaultGrowth);
  const [isChanged, setIsChanged] = useState(false);
  const [changes, setChanges] = useState(na.growth.map((growth_row) => growth_row.change));
  const [totalHeadcounts, setTotalHeadcounts] = useState(
    na.growth.map((growth_row) => growth_row.totalHeadcount),
  );
  const { goToStep } = useStepsWizard({
    naId: na.id,
    preview,
    isExternalAccess,
    accessKey: externalAccessConfig.accessKey,
  });

  useEffect(() => {
    if (isMounted.current) {
      setIsChanged(true);
    } else {
      isMounted.current = true;
    }
    setChanges(na.growth.map((growth_row) => growth_row.change));
    setTotalHeadcounts(na.growth.map((growth_row) => growth_row.totalHeadcount));
  }, [na]);

  const [validables, validator] = useValidator(
    ['headcount', 'startingYear', 'leaseTermInYears', 'errors'],
    () => {
      const message = {};

      const maxUpperStartingYears = validationConfig.maxUpperStartingYears;
      const lowerStartingYear = validationConfig.minStartingYearDate.getFullYear();
      const upperStartingYear = new Date().getFullYear() + maxUpperStartingYears;
      if (
        dateParse(startingYear) === 0 ||
        startingYear.getFullYear() < lowerStartingYear ||
        startingYear.getFullYear() > upperStartingYear
      )
        message.startingYear = t('step3.startingYearError2', {
          maxUpperStartingYears: maxUpperStartingYears,
        });

      if (headcount < 1) message.headcount = t('step3.headcountError');
      const minLeaseYears = validationConfig.minLeaseYears;
      const maxLeaseYears = validationConfig.maxLeaseYears;
      if (!leaseTermInYears || leaseTermInYears < minLeaseYears || leaseTermInYears > maxLeaseYears)
        message.leaseTermInYears = t('step3.leaseTermsError', {
          min: minLeaseYears,
          max: maxLeaseYears,
        });

      return message;
    },
  );

  const updateDataStore = (step) => {
    if (!validator.validate()) return false;

    isChanged && dispatch(actions.na.updateWorkplaceStyleSelected(2));

    if (!isReadOnlyMode) {
      dispatch(actions.na.updateLastStep(step));
      dispatch(isExternalAccess ? updateExternalAccessNA({ hasChanged: isChanged }) : updateNA);
    }

    return true;
  };

  const handleNext = () => {
    if (updateDataStore(paths.steps.spaceYouNeed)) goToStep({ step: paths.steps.spaceYouNeed });
  };
  const handleBack = () => {
    if (updateDataStore(paths.steps.yourPeople)) goToStep({ step: paths.steps.yourPeople });
  };

  return (
    <>
      {isReadOnlyMode && !isExternalAccess && <ExternalClientAccessInfo />}
      <div className={styles.container}>
        <WarningMessage />
        <div className={styles.column1}>
          <HeaderLogo />
          <div className={styles.row2}>
            <div className={styles.row2_column1}>
              <div className={styles.currentsituation}>{t('step3.title')}</div>
              <div className={styles.title}>{t('step3.currentHeadcount')}</div>
              <TextField
                disabled={isReadOnlyMode}
                name="headcount_field"
                className={styles.input}
                value={headcount}
                onChange={(event) => setHeadcount(Number(event.target.value))}
                onBlur={() => {
                  validator.validate('headcount') &&
                    dispatch(actions.na.updateTotalHeadcount(headcount));
                }}
                inputProps={{ 'data-testid': 'Step3HeadCountText', min: 0, max: 1000000 }}
                InputProps={{
                  inputComponent: NumberFormatCustom,
                  endAdornment: (
                    <InputAdornment position="start">
                      <PeopleAltOutlinedIcon />
                    </InputAdornment>
                  ),
                }}
                error={!validables.headcount.valid}
              />
              <Divider variant="fullWidth" className={styles.divider} />
              <div className={styles.title}>{t('step3.startingYear')}</div>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  disabled={isReadOnlyMode}
                  views={['year']}
                  className={styles.input}
                  minDate={validationConfig.minStartingYearDate}
                  value={startingYear}
                  onChange={setStartingYear}
                  onAccept={(value) => {
                    validator.reset('startingYear');
                    dispatch(actions.na.updateStartingYear(dateParse(value)));
                  }}
                  components={{
                    OpenPickerIcon: CalendarIcon,
                  }}
                  inputProps={{
                    'data-testid': 'Step3DatePicker',
                    placeholder: t('step1.yearFormat'),
                  }}
                  renderInput={(params) => (
                    <TextField
                      onBlur={() => {
                        validator.validate('startingYear') &&
                          dispatch(actions.na.updateStartingYear(dateParse(startingYear)));
                      }}
                      className={styles.input}
                      {...params}
                      error={!validables.startingYear.valid}
                    />
                  )}
                />
              </LocalizationProvider>
              <div className={styles.title}>{t('step3.leaseTerms')}</div>
              <TextField
                disabled={isReadOnlyMode}
                name="lease_term_in_years_field"
                className={styles.input}
                value={leaseTermInYears}
                onChange={(event) => setLeaseTermInYears(event.target.value)}
                onBlur={() => {
                  leaseTermInYears < 1 && setLeaseTermInYears(0);
                  validator.validate('leaseTermInYears') &&
                    dispatch(actions.na.updateLeaseTermInYears(Number(leaseTermInYears)));
                }}
                onFocus={() => leaseTermInYears < 1 && setLeaseTermInYears('')}
                inputProps={{ 'data-testid': 'Step3LeaseTermText' }}
                InputProps={{
                  inputComponent: NumberFormatCustom,
                }}
                error={!validables.leaseTermInYears.valid}
              />
              <div className={styles.title}>{t('step3.growthRate')}</div>
              <TextField
                disabled={isReadOnlyMode}
                className={styles.input}
                value={defaultGrowth}
                onChange={(event) => setDefaultGrowth(event.target.value)}
                onBlur={() => dispatch(actions.na.updateDefaultGrowth(Number(defaultGrowth)))}
                inputProps={{
                  'data-testid': 'Step3GrowthRateText',
                  min: -100,
                  max: 100,
                  decimalSeparator: false,
                  allowNegative: true,
                }}
                InputProps={{
                  inputComponent: NumberFormatCustom,
                  endAdornment: (
                    <InputAdornment position="start">
                      <PercentOutlinedIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <div data-testid="Step3ErrorMessage" className={styles.errorline}>
                {validables.headcount.message ||
                  validables.startingYear.message ||
                  validables.leaseTermInYears.message}
              </div>
            </div>
            <div className={styles.row2_column2}>
              <div className={styles.row2_column2_main}>
                <div className={styles.main_cell}>{t('step3.year')}</div>
                <div className={styles.main_cell_border}></div>
                <div className={styles.main_cell}>{t('step3.changePercentage')}</div>
                <div className={styles.main_cell_border}></div>
                <div className={styles.main_cell}>{t('step3.headcountBreakdown')}</div>
                <div className={styles.main_cell_border}></div>
                <div className={styles.main_cell}>{t('step3.total')}</div>
                <div className={styles.main_cell}></div>
              </div>
              <div className={styles.row2_column2_titles}>
                <div className={styles.row2_column2_fields}>
                  <div className={styles.field_cell}></div>
                  <div className={styles.field_cell_border}></div>
                  <div className={styles.field_cell}>{t('step3.value')}</div>
                  <div className={styles.field_cell_border}></div>
                  <div className={styles.headcount_breakdown}>
                    <div className={styles.headcount_breakdown_cell}>{t('step3.office')}</div>
                    <div className={styles.headcount_breakdown_cell}>{t('step3.remote')}</div>
                    <div className={styles.headcount_breakdown_cell}>{t('step3.hybrid')}</div>
                  </div>
                  <div className={styles.field_cell_border}></div>
                  <div className={styles.field_cell}>{t('step3.headcount')}</div>
                  <div className={styles.field_cell}>{t('step3.seats')}</div>
                </div>
              </div>
              {na.growth.map((growth_row, index) => (
                <div key={weakKey(growth_row)}>
                  <div className={styles.row2_column2_fields}>
                    <div className={styles.field_cell}>
                      <TextField
                        disabled
                        inputProps={{
                          className: styles.field_cell_box_disabled,
                        }}
                        value={growth_row.year}
                      />
                    </div>
                    <div className={styles.field_cell_border}></div>
                    <div className={styles.field_cell}>
                      <TextField
                        disabled={isReadOnlyMode}
                        value={changes[index]}
                        onChange={(event) => {
                          const newChanges = [...changes];
                          newChanges[index] = event.target.value;
                          setChanges(newChanges);
                        }}
                        onBlur={() =>
                          dispatch(actions.na.updateGrowthChange(Number(changes[index]), index))
                        }
                        inputProps={{
                          min: -100,
                          max: 100,
                          decimalScale: 0,
                          allowNegative: true,
                          className: styles.field_cell_box_change,
                          enableSuffix: true,
                        }}
                        InputProps={{
                          inputComponent: NumberFormatCustom,
                        }}
                      />
                    </div>
                    <div className={styles.field_cell_border}></div>
                    <div className={styles.headcount_breakdown}>
                      <div className={styles.headcount_breakdown_cell}>
                        <TextField
                          disabled
                          inputProps={{
                            className: styles.headcount_breakdown_cell_disabled,
                          }}
                          value={growth_row.office}
                          InputProps={{
                            inputComponent: NumberFormatCustom,
                          }}
                        />
                      </div>
                      <div className={styles.headcount_breakdown_cell}>
                        <TextField
                          disabled
                          inputProps={{
                            className: styles.headcount_breakdown_cell_disabled,
                          }}
                          value={growth_row.remote}
                          InputProps={{
                            inputComponent: NumberFormatCustom,
                          }}
                        />
                      </div>
                      <div className={styles.headcount_breakdown_cell}>
                        <TextField
                          disabled
                          inputProps={{
                            className: styles.headcount_breakdown_cell_disabled,
                          }}
                          value={growth_row.hybrid}
                          InputProps={{
                            inputComponent: NumberFormatCustom,
                          }}
                        />
                      </div>
                    </div>
                    <div className={styles.field_cell_border}></div>
                    <div className={styles.field_cell}>
                      <TextField
                        disabled={isReadOnlyMode}
                        value={totalHeadcounts[index]}
                        onChange={(event) => {
                          const newTotalHeadcounts = [...totalHeadcounts];
                          newTotalHeadcounts[index] = event.target.value;
                          setTotalHeadcounts(newTotalHeadcounts);
                        }}
                        onBlur={() =>
                          dispatch(
                            actions.na.updateGrowthHeadcount(Number(totalHeadcounts[index]), index),
                          )
                        }
                        inputProps={{
                          min: 0,
                          max: 1000000,
                          className: styles.field_cell_box_headcount,
                        }}
                        InputProps={{
                          inputComponent: NumberFormatCustom,
                        }}
                      />
                    </div>
                    <div className={styles.field_cell}>
                      <TextField
                        disabled
                        inputProps={{
                          className: styles.field_cell_seats,
                        }}
                        value={growth_row.seats}
                        InputProps={{
                          inputComponent: NumberFormatCustom,
                        }}
                      />
                    </div>
                  </div>
                  {index !== na.growth.length - 1 ? (
                    <div className={styles.separator_line}></div>
                  ) : (
                    ''
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className={styles.row3}>
            <Footer
              title={t('step3.footerTitle')}
              description={t('step3.footerDescription')}
              stepsHighlighted="3"
              handleback={handleBack}
              handlenext={handleNext}></Footer>
          </div>
        </div>
        <div className={styles.column2}>
          <Summary />
        </div>
      </div>
    </>
  );
}
